import React from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { motion } from "framer-motion";
import bgImage from "../assets/OptBack1.jpg";
import best1 from "../assets/optAbout1.jpg";
import best2 from "../assets/optAbout2.jpg";
import best3 from "../assets/optAbout3.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const About = () => {
  const professionalStyles = {
    heroSection: {
      background: `linear-gradient(rgba(0, 31, 63, 0.75), rgba(0, 51, 102, 0.75)), url(${bgImage})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      padding: "100px 50px",
      marginBottom: "80px",
      position: "relative",
      overflow: "hidden",
    },
    heroTitle: {
      fontSize: "3.5rem",
      fontWeight: "700",
      textAlign: "center",
      letterSpacing: "-0.5px",
      marginBottom: "50px",
      color: "#fff",
      textTransform: "uppercase",
    },
    contentSection: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "0 30px",
    },
    paragraph: {
      fontSize: "1.1rem",
      lineHeight: "2",
      color: "#fff",
      marginBottom: "30px",
      textAlign: "justify",
      fontWeight: "300",
    },
    sectionTitle: {
      fontSize: "2.5rem",
      fontWeight: "700",
      marginBottom: "60px",
      textAlign: "center",
      position: "relative",
      paddingBottom: "15px",
      borderBottom: "4px solid #003366",
      letterSpacing: "-0.5px",
      textTransform: "uppercase",
      margin: "0 auto 60px",
      display: "table",
    },
    card: {
      border: "none",
      borderLeft: "4px solid #003366",
      background: "#fff",
      transition: "all 0.3s ease",
      height: "100%",
      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      borderRadius: "0",
      overflow: "hidden",
    },
    cardImage: {
      width: "100%",
      height: "260px",
      objectFit: "cover",
      borderRadius: "0",
    },
    cardBody: {
      padding: "35px 30px",
      display: "flex",
      flexDirection: "column",
      minHeight: "200px",
    },
    cardTitle: {
      fontSize: "1.4rem",
      fontWeight: "700",
      marginBottom: "20px",
      color: "#003366",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    cardText: {
      fontSize: "1rem",
      lineHeight: "1.8",
      color: "#555",
    },
    featureSection: {
      background: "#f8f9fa",
      padding: "80px 50px",
      marginTop: "60px",
    },
  };

  return (
    <Container fluid style={{ padding: 0 }}>
      {/* Hero Section with Background Image */}
      <motion.div
        style={professionalStyles.heroSection}
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <motion.h1
          style={professionalStyles.heroTitle}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          About Us
        </motion.h1>

        <div style={professionalStyles.contentSection}>
          <motion.p
            style={professionalStyles.paragraph}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <strong style={{ fontSize: "1.2rem", color: "#fff" }}>
              Optwise Group
            </strong>{" "}
            is a leading independent Australian funds management firm
            specializing in responsible entity, trustee, and investment
            services. We provide a comprehensive suite of investment
            capabilities across wealth management, wealth protection, loan and
            banking, retirement and lifestyle planning.
          </motion.p>
          <motion.p
            style={professionalStyles.paragraph}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            With decades of collective experience in funds management, capital
            markets, and corporate governance, our team works closely with
            global and Australian-listed companies, sovereign wealth funds,
            banks, insurance companies, pension funds, and boutique managers. We
            are committed to delivering innovative and sustainable investment
            solutions that generate long-term value for our clients.
          </motion.p>
          <motion.p
            style={professionalStyles.paragraph}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            As a trusted partner, Optwise Group offers a one-stop solution for
            fund managers, encompassing responsible entity, and fund
            administration. Our expertise, coupled with a strong focus on risk
            management and regulatory compliance, enables us to provide tailored
            investment structures that align with our clients' strategic
            objectives.
          </motion.p>
          <motion.p
            style={professionalStyles.paragraph}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            At Optwise Group, we pride ourselves on our exceptional service,
            deep industry knowledge, and a strong track record of success,
            making us one of the premier funds management firms in Australia.
          </motion.p>
        </div>
      </motion.div>

      {/* What Makes Us the Best Choice Section */}
      <motion.section
        style={professionalStyles.featureSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeInUp}
      >
        <div className="text-center" style={{ width: "100%" }}>
          <motion.h2
            style={professionalStyles.sectionTitle}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What Makes Us the Best Choice
          </motion.h2>
        </div>
        <Container>
          <Row className="g-4 mt-3">
            {[
              {
                title: "Responsible Investment",
                text: "We carefully consider the future impact of every investment decision, ensuring sustainable and ethical outcomes for our clients and the community.",
                image: best1,
              },
              {
                title: "Specialist Funds Management",
                text: "Our experienced team leverages deep market knowledge and proven strategies to maximize value in every investment opportunity.",
                image: best2,
              },
              {
                title: "Tailored Solutions",
                text: "We provide customized investment strategies designed to align with individual client needs, goals, and risk profiles.",
                image: best3,
              },
            ].map((item, index) => (
              <Col md={4} key={index} className="d-flex">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                  whileHover={{
                    y: -8,
                    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                  }}
                  style={{ width: "100%" }}
                >
                  <Card style={professionalStyles.card}>
                    <motion.img
                      src={item.image}
                      alt={item.title}
                      style={professionalStyles.cardImage}
                    />
                    <Card.Body style={professionalStyles.cardBody}>
                      <Card.Title style={professionalStyles.cardTitle}>
                        {item.title}
                      </Card.Title>
                      <Card.Text style={professionalStyles.cardText}>
                        {item.text}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </motion.section>
    </Container>
  );
};

export default About;
